import React, { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  OutlinedInput,
  Dialog,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { useViewport } from "../../../helpers";
import ClearIcon from "@mui/icons-material/Clear";
import CreateIcon from "@mui/icons-material/Create";
import CreatableSelect from "react-select/creatable";
import CheckIcon from "@mui/icons-material/Check";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { QRCode } from "react-qrcode-logo";
import { pictoWebVr } from "../../../helpers";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector } from "react-redux";
import { myExpariencesService } from "../../../services";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GoldenSwitch from "../../../components/goldenSwitch/GoldenSwitch";
import TranslationsEditor from "../../../components/translationsEditor/TranslationsEditor";
import preventDoubleTrad from "../../../helpers/prevent-double-trad";
import Pricing from "../pricing/Pricing.jsx";
import { Clear } from "@mui/icons-material";

const PREFIX = "ExparienceInformations";

const classes = {
  root: `${PREFIX}-root`,
  fullWidthElement: `${PREFIX}-fullWidthElement`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  exparienceLogoContainer: `${PREFIX}-exparienceLogoContainer`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  imageContainer: `${PREFIX}-imageContainer`,
  bottomMarginMobile: `${PREFIX}-bottomMarginMobile`,
  maxInputSize: `${PREFIX}-maxInputSize`,
  marginBottom: `${PREFIX}-marginBottom`,
  infosContainer: `${PREFIX}-infosContainer`,
  centerImage: `${PREFIX}-centerImage`,
  negateMarginTop: `${PREFIX}-negateMarginTop`,
  marginTop: `${PREFIX}-marginTop`,
  entityLogo: `${PREFIX}-entityLogo`,
  entitySelectedLogo: `${PREFIX}-entitySelectedLogo`,
  entityContainer: `${PREFIX}-entityContainer`,
  selectedEntityContainer: `${PREFIX}-selectedEntityContainer`,
  smallEntityContainer: `${PREFIX}-smallEntityContainer`,
  spheresContainer: `${PREFIX}-spheresContainer`,
  modalContent: `${PREFIX}-modalContent`,
  wishChangesContainer: `${PREFIX}-wishChangesContainer`,
  linkColor: `${PREFIX}-linkColor`,
  qrPosition: `${PREFIX}-qrPosition`,
  buttonsQrCode: `${PREFIX}-buttonsQrCode`,
  downloadQRButton: `${PREFIX}-downloadQRButton`,
  shareIconButton: `${PREFIX}-shareIconButton`,
  copyQrUrlButton: `${PREFIX}-copyQrUrlButton`,
  togglePromotedLogoContainer: `${PREFIX}-togglePromotedLogoContainer`,
  uploadPromotedLogoContainer: `${PREFIX}-uploadPromotedLogoContainer`,
  uploadPromotedLogoTitle: `${PREFIX}-uploadPromotedLogoTitle`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  loadingIcon: `${PREFIX}-loadingIcon`,
  videoAccordionContainer: `${PREFIX}-videoAccordionContainer`,
  promotedVideo: `${PREFIX}-promotedVideo`,
  uploadVideoButton: `${PREFIX}-uploadVideoButton`,
  promotedVideoAccordion: `${PREFIX}-promotedVideoAccordion`,
  minimizeEntityTitle: `${PREFIX}-minimizeEntityTitle`,
  accordionColor: `${PREFIX}-accordionColor`,
  accordionDetails: `${PREFIX}-accordionDetails`,
  deletePromotedContentIcon: `${PREFIX}-deletePromotedContentIcon`,
  deletePromotedContentLogoIcon: `${PREFIX}-deletePromotedContentLogoIcon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.fullWidthElement}`]: {
    width: "100%",
  },

  [`& .${classes.paintingContainer}`]: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "1rem",
    minWidth: "50%",
    maxWidth: "50%",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
      minWidth: "100%",
      maxWidth: "100%",
    },
  },

  [`& .${classes.exparienceLogoContainer}`]: {
    maxWidth: "370px",
    width: "100%",
    borderRadius: "10px",
  },

  [`& .${classes.editImageIcon}`]: {
    position: "absolute",
    right: "0",
    top: "0",
  },

  [`& .${classes.imageContainer}`]: {
    position: "relative",
    maxWidth: "370px",
    marginTop: "1rem",
  },

  [`& .${classes.bottomMarginMobile}`]: {
    maxWidth: "350px",
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "calc(56px + 1rem)",
    },
  },

  [`& .${classes.maxInputSize}`]: {
    width: "350px",
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.infosContainer}`]: {
    [theme.breakpoints.up("sm")]: {
      padding: "3rem 0 2rem 1rem",
      minWidth: "50%",
      maxWidth: "50%",
      margin: "0 auto",
    },
  },

  [`& .${classes.centerImage}`]: {
    margin: "0 auto",
  },

  [`& .${classes.negateMarginTop}`]: {
    marginTop: "-0.5rem",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.entityLogo}`]: {
    width: "80px",
    height: "80px",
  },

  [`& .${classes.entitySelectedLogo}`]: {
    width: "100px",
    height: "100px",
  },

  [`& .${classes.entityContainer}`]: {
    maxWidth: "650px",
    position: "relative",
    cursor: "pointer",
    margin: "1rem 0",
  },

  [`& .${classes.selectedEntityContainer}`]: {
    textAlign: "center",
    position: "relative",
    margin: "1rem 0",
  },

  [`& .${classes.smallEntityContainer}`]: {
    width: "150px",
    height: "120px",
    cursor: "pointer",
    marginBottom: "1rem",
    textAlign: "center",
  },

  [`& .${classes.spheresContainer}`]: {
    height: "400px",
    margin: "1rem 0",
    overflowY: "auto",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    paddingTop: "1rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.modalContent}`]: {
    padding: "0 1rem",
  },

  [`& .${classes.wishChangesContainer}`]: {
    margin: "1rem 0",
    maxWidth: "350px",
    textAlign: "center",
  },

  [`& .${classes.linkColor}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.qrPosition}`]: {
    display: "flex",
    justifyContent: "center",
    marginTop: "2rem",
  },

  [`& .${classes.buttonsQrCode}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.downloadQRButton}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.shareIconButton}`]: {
    color: "#343434",
    backgroundColor: theme.palette.primary.main,
    width: "40px",
    height: "40px",
    margin: "1rem 0",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "#343434",
    },
  },

  [`& .${classes.copyQrUrlButton}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.togglePromotedLogoContainer}`]: {
    textAlign: "center",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.uploadPromotedLogoContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "1rem",
  },

  [`& .${classes.uploadPromotedLogoTitle}`]: {
    marginBottom: "1rem !important",
  },

  [`& .${classes.loaderContainer}`]: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#484848",
    padding: "1rem",
    borderRadius: "12px",
    minWidth: "300px",
    textAlign: "center",
  },

  [`& .${classes.loadingIcon}`]: {
    margin: "1rem auto",
  },

  [`& .${classes.videoAccordionContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.promotedVideo}`]: {
    marginTop: "1rem",
    width: "100%",
  },

  [`& .${classes.uploadVideoButton}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.promotedVideoAccordion}`]: {
    backgroundColor: "#363636 !important",
  },

  [`& .${classes.minimizeEntityTitle}`]: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  [`& .${classes.accordionColor}`]: {
    backgroundColor: "#363636",
    backgroundImage: "none",
    [theme.breakpoints.up("md")]: {
      width: "350px",
    },
  },

  [`& .${classes.accordionDetails}`]: {
    width: "100%",

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },

  [`& .react-select__input`]: {
    color: "white !important",
  },

  [`& .${classes.deletePromotedContentIcon}`]: {
    position: "absolute",
    zIndex: 999,
    top: "5px",
    right: "5px",
    width: "40px",
    marginLeft: "1rem",
    marginTop: "1rem",
    backgroundColor: "#D3AF5F",
    color: "#484848",
    "&:hover": {
      color: "#D3AF5F",
      backgroundColor: "#303030",
    },
  },

  [`& .${classes.deletePromotedContentLogoIcon}`]: {
    position: "absolute",
    zIndex: 999,
    top: "-10px",
    left: "-10px",
    width: "40px",
    marginLeft: "1rem",
    marginTop: "1rem",
    backgroundColor: "#D3AF5F",
    color: "#484848",
    "&:hover": {
      color: "#D3AF5F",
      backgroundColor: "#303030",
    },
  },
}));

const ExparienceInformations = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const [urls, setUrls] = useState({});
  const [tagsInput, setTagsInput] = useState("");
  const [editEntity, setEditEntity] = useState(false);
  const [copied, setCopied] = useState(false);
  const [picto, setPicto] = useState(null);
  const [promotedLogo, setPromotedLogo] = useState(false);
  const [savingPromoted, setSavingPromoted] = useState(false);
  const canvasRef = useRef(null);

  const isSuperAdmin = useSelector(
    (state) => state.user.currentUser?.account?.isSuperAdmin
  );

  const downloadQR = () => {
    let canvas = canvasRef.current.querySelector("canvas");
    let image = canvas.toDataURL("image/jpg");
    let anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = `QRCode-${
      props.exparience.displayName
        ? props.exparience.displayName
        : props.exparience.name
    }.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  function handleImageReader(event, id) {
    let tmp = { ...urls };
    tmp[id] = URL.createObjectURL(event.target.files[0]);
    setUrls(tmp);
  }

  useEffect(() => {
    setPicto(pictoWebVr);
  }, []);

  useEffect(() => {
    if (props.files.exparienceLogo) {
      let tmp = { ...urls };
      tmp["exparienceLogo"] = URL.createObjectURL(
        props.files.exparienceLogo[0]
      );
      setUrls(tmp);
    }
  }, [props.files]);

  const handleDeleteTranslation = (transIndex) => {
    let tmp = { ...props.exparience };
    const tmp2 = [...tmp.description.translations];
    tmp2.splice(transIndex, 1);
    tmp.description = {
      ...tmp.description,
      translations: tmp2,
    };
    props.setExparience(tmp);
  };

  const handleModifyTranslation = (value, key, transIndex) => {
    let tmp = { ...props.exparience };
    const tmp2 = [...tmp.description.translations];
    tmp2[transIndex] = { ...tmp2[transIndex], [key]: value };
    tmp.description = {
      ...tmp.description,
      translations: tmp2,
    };
    props.setExparience(tmp);
  };

  const handleAddTranslation = (value, key) => {
    const tmp = {
      ...props.exparience,
      description: props.exparience.description || {
        translations: [],
        initial: "",
      },
    };
    if (key === "translations") {
      const myObj = [...tmp.description[key]];
      const langsArray = preventDoubleTrad(myObj, value);
      tmp["description"] = { ...tmp.description, [key]: langsArray };
    } else {
      tmp[key] = { ...tmp[key], initial: value };
    }
    props.setExparience(tmp);
  };

  const handleMoveTraduction = (fromIndex, toIndex) => {
    if (
      toIndex >= 0 &&
      toIndex < props.exparience.description.translations.length
    ) {
      let tmp = { ...props.exparience };
      const myObj = [...tmp.description.translations];
      const tmpElem = myObj[fromIndex];
      myObj.splice(fromIndex, 1);
      myObj.splice(toIndex, 0, tmpElem);
      tmp = {
        ...tmp,
        description: { ...tmp.description, translations: myObj },
      };
      props.setExparience(tmp);
    }
  };

  return (
    <Root>
      <div className={classes.root}>
        <Dialog
          onClose={() => setEditEntity(false)}
          aria-labelledby="simple-dialog-title"
          open={editEntity}
        >
          <Root>
            <div className={clsx(classes.marginTop, classes.modalContent)}>
              <Typography variant="h5">
                {t("exparienceCreateForm.entitySelect")}
              </Typography>
              <div className={classes.spheresContainer}>
                {props.entities?.map((entity) => {
                  return (
                    <div
                      key={entity.id}
                      className={classes.smallEntityContainer}
                      style={{
                        border:
                          entity.id === props.exparience.entityId &&
                          "1px solid white",
                        opacity:
                          props.exparience?.entityId !== "" &&
                          props.exparience?.entityId?.length !== 0 &&
                          entity &&
                          entity.id !== props.exparience.entityId &&
                          "0.2",
                      }}
                      onClick={(e) => {
                        const tmp = props.entities.find(
                          (ent) => ent.id === entity.id
                        );
                        props.setExparience({
                          ...props.exparience,
                          entityId: tmp.id,
                          entity: tmp,
                        });
                        setEditEntity(false);
                      }}
                    >
                      <img
                        id="entityLogo"
                        className={classes.entityLogo}
                        alt={entity.name}
                        src={entity.logo}
                      />
                      <Typography
                        variant="body1"
                        className={classes.minimizeEntityTitle}
                      >
                        {entity.name}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* <DialogActions>
          <Button vairant="contained" onClick={() => setEditEntity(false)} color="primary">
            {t('exparienceCreateForm.saveMenu')}
          </Button>
        </DialogActions> */}
          </Root>
        </Dialog>
        <div className={classes.paintingContainer}>
          <div className={classes.centerImage}>
            {isSuperAdmin === 1 && (
              <div className={classes.togglePromotedLogoContainer}>
                <GoldenSwitch
                  checked={promotedLogo}
                  onChange={(e) => setPromotedLogo(e.target.checked)}
                  disabled={savingPromoted}
                  name="promotedLogo"
                  label={
                    !promotedLogo
                      ? t("myExpariences.form.exparienceLogo")
                      : t("myExpariences.form.promotedExparienceLogo")
                  }
                />
              </div>
            )}
            {isSuperAdmin !== 1 && (
              <Typography variant="h6">
                {t("myExpariences.form.exparienceLogo")}
              </Typography>
            )}
            <div className={classes.imageContainer}>
              {savingPromoted ? (
                <div className={classes.loaderContainer}>
                  <CircularProgress
                    size={50}
                    color="primary"
                    className={classes.loadingIcon}
                  />
                  <Typography variant="body1">
                    {t("loaders.uploading")}
                  </Typography>
                </div>
              ) : props.exparience.logoUrl?.length === 0 &&
                !props.files.exparienceLogo ? (
                <div className={classes.uploadPromotedLogoContainer}>
                  <Typography
                    variant="body2"
                    className={classes.uploadPromotedLogoTitle}
                  >
                    {t("myDesign.informations.noPicture")}
                  </Typography>
                  {isSuperAdmin === 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      component="label"
                      className={classes.uploadButton}
                      onChange={(e) => {
                        handleImageReader(e, "exparienceLogo");
                        props.setFiles({
                          ...props.files,
                          exparienceLogo: e.target.files,
                        });
                        props.setExparience({
                          ...props.exparience,
                          logo: e.target.files[0].name,
                        });
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("myDesign.zones.uploadVideo.selectFileButton")}
                      <input
                        type="file"
                        accept="image/*"
                        name="expariencePromotedLogo"
                        hidden
                      />
                    </Button>
                  )}
                </div>
              ) : !promotedLogo ? (
                <img
                  id="exparienceLogo"
                  className={classes.exparienceLogoContainer}
                  alt={
                    (urls.exparienceLogo && urls.exparienceLogo) ||
                    (props.exparience && props.exparience.logoUrl)
                      ? "exparience logo"
                      : null
                  }
                  src={
                    (urls.exparienceLogo ? urls.exparienceLogo : undefined) ||
                    (props.exparience && props.exparience.logoUrl
                      ? props.exparience.logoUrl
                      : undefined)
                  }
                />
              ) : props.exparience.promotedLogoUrl ? (
                <img
                  id="promotedLogo"
                  className={classes.exparienceLogoContainer}
                  alt={"exparience promoted logo"}
                  src={props.exparience.promotedLogoUrl}
                />
              ) : (
                <div className={classes.uploadPromotedLogoContainer}>
                  <Typography
                    variant="body2"
                    className={classes.uploadPromotedLogoTitle}
                  >
                    {t("myDesign.informations.noPicture")}
                  </Typography>
                  {isSuperAdmin === 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      component="label"
                      className={classes.uploadButton}
                      onChange={(e) => {
                        setSavingPromoted(true);
                        myExpariencesService
                          .uploadPromotedLogo(
                            props.exparience._id,
                            e.target.files[0]
                          )
                          .then((res) => {
                            props.setExparience({
                              ...props.exparience,
                              promotedLogoUrl: res.url.promotedLogoUrl,
                            });
                            enqueueSnackbar(
                              t("toasts.associateBorderlessImageSuccess"),
                              { variant: "success" }
                            );
                            setSavingPromoted(false);
                          })
                          .catch((err) => {
                            if (err === 403)
                              enqueueSnackbar(t("toasts.unauthorizedError"), {
                                variant: "error",
                              });
                            else
                              enqueueSnackbar(
                                t("toasts.associateBorderlessImageError"),
                                { variant: "error" }
                              );
                            setSavingPromoted(false);
                          });
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("myDesign.zones.uploadVideo.selectFileButton")}
                      <input
                        type="file"
                        accept="image/*"
                        name="expariencePromotedLogo"
                        hidden
                      />
                    </Button>
                  )}
                </div>
              )}
              {props.exparience.promotedLogoUrl && promotedLogo && (
                <IconButton
                  className={classes.deletePromotedContentLogoIcon}
                  onClick={() => {
                    myExpariencesService
                      .deletePromotedLogo(props.exparience._id)
                      .then((res) => {
                        props.setExparience({
                          ...props.exparience,
                          promotedLogoUrl: null,
                        });
                        enqueueSnackbar(
                          t("toasts.deleteBorderlessImageSuccess"),
                          { variant: "success" }
                        );
                        setSavingPromoted(false);
                      })
                      .catch((err) => {
                        if (err === 403)
                          enqueueSnackbar(t("toasts.unauthorizedError"), {
                            variant: "error",
                          });
                        else
                          enqueueSnackbar(
                            t("toasts.deleteBorderlessImageError"),
                            { variant: "error" }
                          );
                        setSavingPromoted(false);
                      });
                  }}
                >
                  <Clear />
                </IconButton>
              )}
              <div className={classes.editImageIcon}>
                {(!savingPromoted &&
                  promotedLogo &&
                  props.exparience.promotedLogoUrl) ||
                (!promotedLogo &&
                  (urls.exparienceLogo ||
                    (props.exparience && props.exparience.logoUrl))) ? (
                  <Box>
                    <input
                      type="file"
                      accept="image/*"
                      name="myDesignImage"
                      id="icon-button-file"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        if (promotedLogo && props.exparience.promotedLogoUrl) {
                          setSavingPromoted(true);
                          myExpariencesService
                            .uploadPromotedLogo(
                              props.exparience._id,
                              e.target.files[0]
                            )
                            .then((res) => {
                              props.setExparience({
                                ...props.exparience,
                                promotedLogoUrl: res.url.promotedLogoUrl,
                              });
                              enqueueSnackbar(
                                t("toasts.associateBorderlessImageSuccess"),
                                { variant: "success" }
                              );
                              setSavingPromoted(false);
                            })
                            .catch((err) => {
                              if (err === 403)
                                enqueueSnackbar(t("toasts.unauthorizedError"), {
                                  variant: "error",
                                });
                              else
                                enqueueSnackbar(
                                  t("toasts.associateBorderlessImageError"),
                                  { variant: "error" }
                                );
                              setSavingPromoted(false);
                            });
                        } else {
                          handleImageReader(e, "exparienceLogo");
                          props.setFiles({
                            ...props.files,
                            exparienceLogo: e.target.files,
                          });
                          props.setExparience({
                            ...props.exparience,
                            logo: e.target.files[0].name,
                          });
                        }
                      }}
                    />
                    <label htmlFor="icon-button-file">
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        size="large"
                      >
                        <CreateIcon />
                      </IconButton>
                    </label>
                  </Box>
                ) : null}
              </div>
              {promotedLogo && (
                <Accordion className={classes.promotedVideoAccordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="promoted-video-panel"
                    id="promoted-video-header"
                  >
                    <Typography>
                      {t("myExpariences.form.promotedVideo")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      {t("myExpariences.form.promotedVideoDescription")}
                    </Typography>
                    <div className={classes.videoAccordionContainer}>
                      <Button
                        variant="contained"
                        color="primary"
                        component="label"
                        className={clsx(
                          classes.uploadButton,
                          classes.uploadVideoButton
                        )}
                        sx={{ width: "100%" }}
                        onChange={(e) => {
                          setSavingPromoted(true);
                          myExpariencesService
                            .uploadPromotedVideo(
                              props.exparience._id,
                              e.target.files[0]
                            )
                            .then((res) => {
                              props.setExparience({
                                ...props.exparience,
                                promotedVideoUrl: res.url.promotedVideoUrl,
                              });
                              enqueueSnackbar(
                                t("toasts.promotedVideoUploadSuccess"),
                                { variant: "success" }
                              );
                              setSavingPromoted(false);
                            })
                            .catch((err) => {
                              if (err === 403)
                                enqueueSnackbar(t("toasts.unauthorizedError"), {
                                  variant: "error",
                                });
                              else
                                enqueueSnackbar(
                                  t("toasts.promotedVideoUploadFailed"),
                                  { variant: "error" }
                                );
                              setSavingPromoted(false);
                            });
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {t("myDesign.zones.uploadVideo.selectVideoButton")}
                        <input
                          type="file"
                          accept="video/*"
                          name="expariencePromotedVideo"
                          hidden
                        />
                      </Button>
                      {props.exparience.promotedVideoUrl && (
                        <Box sx={{ position: "relative" }}>
                          <IconButton
                            className={classes.deletePromotedContentIcon}
                            onClick={() => {
                              myExpariencesService
                                .deletePromotedVideo(props.exparience._id)
                                .then((res) => {
                                  props.setExparience({
                                    ...props.exparience,
                                    promotedVideoUrl: null,
                                  });
                                  enqueueSnackbar(
                                    t("toasts.promotedVideoDeletedSuccess"),
                                    { variant: "success" }
                                  );
                                  setSavingPromoted(false);
                                })
                                .catch((err) => {
                                  if (err === 403)
                                    enqueueSnackbar(
                                      t("toasts.unauthorizedError"),
                                      {
                                        variant: "error",
                                      }
                                    );
                                  else
                                    enqueueSnackbar(
                                      t("toasts.promotedVideoDeletedFailed"),
                                      { variant: "error" }
                                    );
                                  setSavingPromoted(false);
                                });
                            }}
                          >
                            <Clear />
                          </IconButton>
                          <video
                            src={props.exparience.promotedVideoUrl}
                            controls
                            className={classes.promotedVideo}
                          />
                        </Box>
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
          </div>
        </div>
        <div className={classes.infosContainer}>
          <div className={classes.fullWidthElement}>
            {/* <div>
            <FormControl variant="outlined" className={width <= 1024 ? classes.fullWidthElement : ""}>
              <InputLabel htmlFor="name">{t('myExpariences.form.name')}</InputLabel>
              <OutlinedInput
                disabled
                className={clsx(classes.marginBottom, width > 1024 ? classes.maxInputSize : "")}
                label={t('myExpariences.form.name')}
                value={props.exparience.name}
                fullWidth
                variant={"outlined"}
              />
            </FormControl>
          </div> */}
            <div
              className={clsx(classes.contentContainer, classes.spacingBottom)}
            >
              <FormControl
                variant="outlined"
                className={clsx(classes.fullWidthElement, classes.inputSize)}
              >
                <InputLabel htmlFor="name">
                  {t("myExpariences.form.name")}
                </InputLabel>
                <OutlinedInput
                  name="name"
                  error={
                    props.isNameUsed ||
                    (props.isNameUsed === false && props.name.length === 0) ||
                    /[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(props.name)
                  }
                  className={clsx(
                    classes.marginBottom,
                    width > 1024 ? classes.maxInputSize : ""
                  )}
                  label={t("myExpariences.form.name")}
                  value={props.name}
                  onChange={(e) => {
                    props.setName(e.target.value);
                  }}
                  fullWidth
                  required
                  variant={"outlined"}
                  endAdornment={
                    (props.isNameUsed !== null || props.isNameSearching) && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="check if name is available"
                          size="large"
                        >
                          {props.isNameSearching ? (
                            <CircularProgress />
                          ) : props.isNameUsed ||
                            (props.isNameUsed === false &&
                              props.name.length === 0) ||
                            (props.name.length > 0 &&
                              /[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(
                                props.name
                              )) ? (
                            <ClearIcon className={classes.redIcon} />
                          ) : (
                            <CheckIcon className={classes.greenIcon} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>
              {props.isNameUsed && (
                <FormHelperText
                  id="nameUsed"
                  error
                  className={clsx(
                    classes.negateMarginTop,
                    classes.marginBottom
                  )}
                >
                  {t("myExpariences.formErrors.nameUsed")}
                </FormHelperText>
              )}
              {props.isNameUsed === false && props.name.length === 0 && (
                <FormHelperText
                  id="nameRequired"
                  error
                  className={clsx(
                    classes.negateMarginTop,
                    classes.marginBottom
                  )}
                >
                  {t("myExpariences.formErrors.requiredField")}
                </FormHelperText>
              )}
              {/[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(props.name) && (
                <FormHelperText
                  id="nameRequired"
                  error
                  className={clsx(
                    classes.negateMarginTop,
                    classes.marginBottom
                  )}
                >
                  {t("myExpariences.formErrors.incorrectNameCaracters")}
                </FormHelperText>
              )}
            </div>
          </div>
          <div className={classes.fullWidthElement}>
            <div>
              <FormControl
                variant="outlined"
                className={width <= 1024 ? classes.fullWidthElement : ""}
              >
                <InputLabel htmlFor="displayName">
                  {t("myExpariences.form.displayName")}
                </InputLabel>
                <OutlinedInput
                  onChange={(e) =>
                    props.setExparience({
                      ...props.exparience,
                      displayName: e.target.value,
                    })
                  }
                  className={clsx(
                    classes.marginBottom,
                    width > 1024 ? classes.maxInputSize : ""
                  )}
                  label={t("myExpariences.form.displayName")}
                  value={props.exparience.displayName}
                  fullWidth
                  required
                  variant={"outlined"}
                />
              </FormControl>
            </div>
          </div>
          <div className={classes.marginBottom}>
            {/* <TextField
              name="descriptionApp"
              label={t("myExpariences.form.descriptionMobileApp")}
              className={width > 1024 ? classes.maxInputSize : ""}
              onChange={(e) =>
                props.setExparience({
                  ...props.exparience,
                  descriptionApp: e.target.value,
                })
              }
              value={props.exparience.descriptionApp}
              fullWidth={width <= 1024}
              multiline
              variant={"outlined"}
            /> */}
            <Accordion className={classes.accordionColor}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>
                  {t("myExpariences.form.addDescription")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TranslationsEditor
                  elements={props.exparience?.description?.translations}
                  defaultText={props.exparience?.description?.initial}
                  defaultTextKey="description"
                  handleDefaultText={handleAddTranslation}
                  addTranslation={handleAddTranslation}
                  removeTranslation={handleDeleteTranslation}
                  handleTranslation={handleModifyTranslation}
                  configurator
                  translate
                  displayButtonVerticaly
                  moveTraduction={handleMoveTraduction}
                />
              </AccordionDetails>
            </Accordion>

            {!props.isAdmin && (
              <div className={classes.wishChangesContainer}>
                <Typography variant="body1">
                  {t("myExpariences.form.wantModification")}
                  <a
                    className={classes.linkColor}
                    href="mailto:hello@artdesignstory.fr"
                  >
                    hello@artdesignstory.fr
                  </a>
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.bottomMarginMobile}>
            {props.isAdmin && (
              <div className={classes.entityContainer}>
                <Typography variant="h5">
                  {t("myDesign.campaigns.campaignForm.linkedEntity")}
                </Typography>
                <div className={classes.selectedEntityContainer}>
                  <img
                    id="entityLogo"
                    className={classes.entitySelectedLogo}
                    style={{
                      display:
                        (!props.exparience ||
                          !props.exparience.entity ||
                          !props.exparience.entity.logo) &&
                        "none",
                    }}
                    alt={
                      props.exparience && props.exparience.entity
                        ? props.exparience.entity.name
                        : null
                    }
                    src={
                      props.exparience && props.exparience.entity
                        ? props.exparience.entity.logo
                        : undefined
                    }
                  />
                  <Typography variant="body1">
                    {props.exparience?.entity?.name}
                  </Typography>
                  <input
                    name="sphereLogo"
                    id="icon-button-sphere-add"
                    style={{ display: "none" }}
                  />
                  {!props.exparience?.entityId && props.isAdmin && (
                    <label htmlFor="icon-button-sphere-add">
                      <Button
                        variant="contained"
                        color="primary"
                        component="span"
                        onClick={(e) => setEditEntity(true)}
                      >
                        {t("exparienceCreateForm.selectSphere")}
                      </Button>
                    </label>
                  )}
                  <Pricing
                    exparience={props.exparience}
                    setExparience={props.setExparience}
                    isAdmin={props.isAdmin}
                    offerings={props.offerings}
                  />
                  <div className={classes.editImageIcon}>
                    <input
                      name="sphereLogo"
                      id="icon-button-edit-sphere"
                      style={{ display: "none" }}
                    />
                    {props.exparience?.entityId && props.isAdmin && (
                      <label htmlFor="icon-button-ground-logo">
                        <IconButton
                          color="primary"
                          aria-label="upload ground logo"
                          component="span"
                          style={{
                            position: "absolute",
                            top: "-2px",
                            right: "2rem",
                          }}
                          onClick={(e) => setEditEntity(true)}
                          size="large"
                        >
                          <CreateIcon />
                        </IconButton>
                      </label>
                    )}
                  </div>
                  <div className={classes.editImageIcon}>
                    <input
                      name="sphereDelete"
                      id="icon-button-sphere-delete"
                      style={{ display: "none" }}
                    />
                    {props.exparience?.entityId && props.isAdmin && (
                      <label htmlFor="icon-button-sphere-delete">
                        <IconButton
                          color="primary"
                          aria-label="clear sphere logo"
                          component="span"
                          onClick={(e) =>
                            props.setExparience({
                              ...props.exparience,
                              entityId: "",
                              entity: null,
                            })
                          }
                          size="large"
                        >
                          <ClearIcon />
                        </IconButton>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className={clsx(classes.marginTop, classes.marginBottom)}>
              <Typography variant="h5" className={classes.marginBottom}>
                {t("myExpariences.form.tags")}
              </Typography>
              <CreatableSelect
                isClearable
                isDisabled={!props.isAdmin}
                defaultValue={
                  props.exparience &&
                  props.exparience.tags &&
                  props.exparience.tags.map((item) => {
                    return {
                      value: item,
                      label: item,
                      name: item,
                    };
                  })
                }
                isMulti
                formatCreateLabel={(inputValue) =>
                  `${t("myExpariences.form.addTag")} ${inputValue}`
                }
                noOptionsMessage={() => t("account.form.noResults")}
                getOptionLabel={(option) =>
                  option.label ? option.label : option.name
                }
                placeholder={t("myExpariences.form.addTagPlaceholder")}
                onKeyDown={(e) => {
                  if (!tagsInput) return;
                  switch (e.key) {
                    case "Enter":
                      setTagsInput("");
                      break;
                    case "Tab":
                      setTagsInput("");
                      break;
                    default:
                      setTagsInput("");
                  }
                }}
                onInputChange={(value) => setTagsInput(value)}
                onChange={(newValue, actionMeta) => {
                  if (
                    actionMeta.action !== "pop-value" &&
                    actionMeta.action !== "remove-value" &&
                    actionMeta.action !== "clear"
                  ) {
                    const tmpValue = [...newValue];
                    tmpValue.forEach((item) => {
                      item.label = item.value;
                      item.name = item.value;
                    });
                    props.setExparience({
                      ...props.exparience,
                      tags: tmpValue,
                    });
                  } else {
                    props.setExparience({
                      ...props.exparience,
                      tags: newValue,
                    });
                  }
                }}
                classNamePrefix="react-select"
              />
            </div>
            <div>
              <div className={classes.qrPosition}>
                {props.exparience.arLink && picto && (
                  <CopyToClipboard
                    text={props.exparience.arLink}
                    onCopy={() => {
                      setCopied(true);
                      enqueueSnackbar(t("toasts.copiedInClipboard"), {
                        variant: "info",
                      });
                    }}
                  >
                    <div ref={canvasRef}>
                      <QRCode
                        id="react-qrcode-logo"
                        value={props.exparience.arLink}
                        size={250}
                        ecLevel="H"
                        bgColor="#000000"
                        fgColor="#D3AF5F" //golden
                        includeMargin={true}
                        qrStyle="dots"
                        logoImage={picto}
                        logoHeight={65}
                        logoWidth={65}
                        eyeRadius={[
                          [0, 100, 100, 100],
                          [100, 0, 100, 100],
                          [100, 100, 100, 0],
                        ]}
                        logoOpacity={1} // the higher the opacity is, the lower the logo has to be
                      />
                    </div>
                  </CopyToClipboard>
                )}
              </div>
              {props.exparience.arLink && picto && (
                <div>
                  <div className={classes.buttonsQrCode}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={downloadQR}
                      className={classes.downloadQRButton}
                    >
                      {t("myDesign.webVR.downloadQR")}
                    </Button>
                    <a
                      href={props.exparience.arLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconButton
                        className={classes.shareIconButton}
                        size="large"
                      >
                        <LinkIcon />
                      </IconButton>
                    </a>
                  </div>
                  <div className={classes.copyQrUrlButton}>
                    <CopyToClipboard
                      text={props.exparience.arLink}
                      onCopy={() => {
                        setCopied(true);
                        enqueueSnackbar(t("toasts.copiedInClipboard"), {
                          variant: "info",
                        });
                      }}
                    >
                      <Button variant="contained" color="primary">
                        {t("myDesign.webVR.copyShortLink")}
                      </Button>
                    </CopyToClipboard>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Root>
  );
};

export default ExparienceInformations;
